import axios from 'axios';
import { Keys, locale } from 'src/i18n';
import { setIsAuthenticated } from 'src/models/auth/slice';
import { clearCookies } from 'src/modules/stepper/queries';
import { isSelfServe, isWebPlatform } from '../utils';

// import type { AxiosResponse } from 'axios';

const axiosInstance = axios.create();

const errorDescriptionHandlers = new Set<(description: string) => void>();

export const setAxiosErrorDescriptionHandler = (
  handler: (description: string) => void,
) => {
  errorDescriptionHandlers.add(handler);
  return () => {
    errorDescriptionHandlers.delete(handler);
  };
};

export const isWorkflowApiRequest = (error) =>
  [
    error?.config?.url,
    error?.response?.config?.url,
    error?.request?.responseURL,
  ].some((url) => `${url || ''}`.includes('/vymo/v3/workflow'));

const handleError = (error) => {
  const errorDescription =
    error?.response?.data?.message || locale(Keys.SOMETHING_WENT_WRONG);

  if (isWorkflowApiRequest(error)) {
    errorDescriptionHandlers.forEach((handler) => {
      handler(errorDescription);
    });
  }

  if (
    error?.response?.data?.authentication_error === 'unauthorized' &&
    isWebPlatform()
  ) {
    clearCookies();
    localStorage.clear();
    setIsAuthenticated(false);
    window.location.href = '/';
  }
  return Promise.reject(error);
};

let authToken = '';

export const setAxiosInstanceAuthToken = (auth_token: string) => {
  authToken = auth_token;
};

const requestInterceptor = (config) => {
  if (authToken) {
    config.headers['X-vymo-auth-token'] = authToken;
  }
  if (isSelfServe()) {
    config.headers['Ss-ui'] = true;
  }
  return config;
};

const responseInterceptor = (response) => {
  if (
    response?.data?.authentication_error === 'unauthorized' &&
    isWebPlatform() &&
    authToken
  ) {
    clearCookies();
    localStorage.clear();
    setIsAuthenticated(false);
    window.location.href = '/';
  }
  if ('statusText' in response) {
    if (response.status === 200 || response.statusText === 'OK') {
      return {
        data: response.data,
        headers: response.headers,
        status: response.status,
      };
    }
    return Promise.reject(response);
  }
  return response;
};

axiosInstance.interceptors.request.use(requestInterceptor, handleError);

axiosInstance.interceptors.response.use(responseInterceptor, handleError);

// @ts-ignore
export default axiosInstance;

export type AxiosResponseV1 = any;
